import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { QueryState } from "@/shared/ui/QueryState";
import { Card } from "@devhop/ui";
import { Building, UserCheck, Users } from "lucide-react";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
} from "reactflow";
import "reactflow/dist/style.css";
import type { PositionWithDetails } from "../../domain/types";
import { useOrgChartOverview } from "../api/queries";
import { OrgChartNodeComponent } from "../ui/OrgChartNode";
import { OrgChartSidebar } from "../ui/OrgChartSidebar";

const nodeTypes = {
  position: OrgChartNodeComponent,
};

export function OrgChartOverviewPage() {
  const { data, isLoading, error } = useOrgChartOverview({});

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedPosition, setSelectedPosition] =
    useState<PositionWithDetails | null>(null);

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedPosition(node.data.position);
  }, []);

  const layoutNodes = useCallback((rawNodes: Node[]) => {
    const levelMap = new Map<number, Node[]>();
    const positionedNodes: Node[] = [];
    const seenIds = new Set<string>();

    rawNodes.forEach((node) => {
      if (seenIds.has(node.id)) return;
      seenIds.add(node.id);

      const chartX = node.data.position.chartX;
      const chartY = node.data.position.chartY;
      const hasSavedPosition =
        chartX !== null &&
        chartX !== undefined &&
        chartY !== null &&
        chartY !== undefined;

      if (hasSavedPosition) {
        positionedNodes.push({
          ...node,
          position: node.position,
        });
        return;
      }

      const level = node.data.level;
      let bucket = levelMap.get(level);
      if (!bucket) {
        bucket = [];
        levelMap.set(level, bucket);
      }
      bucket.push(node);
    });

    levelMap.forEach((levelNodes, level) => {
      const nodeWidth = 160;
      const horizontalSpacing = 30;
      const totalWidth =
        levelNodes.length * nodeWidth +
        (levelNodes.length - 1) * horizontalSpacing;
      const startX = -totalWidth / 2;

      levelNodes.forEach((node, index) => {
        positionedNodes.push({
          ...node,
          position: {
            x: startX + index * (nodeWidth + horizontalSpacing),
            y: (level - 1) * 200,
          },
        });
      });
    });

    return positionedNodes;
  }, []);

  useMemo(() => {
    if (data) {
      const positionedNodes = layoutNodes(data.nodes);
      setNodes(positionedNodes);
      setEdges(data.edges);
    }
  }, [data, layoutNodes, setNodes, setEdges]);

  const stats = useMemo(() => {
    if (!data) return null;
    return {
      totalEmployees: data.totalEmployees,
      totalPositions: data.totalPositions,
      totalDepartments: data.totalDepartments,
    };
  }, [data]);

  if (isLoading) {
    return <QueryState isLoading>ກຳລັງໂຫຼດຜັງອົງກອນ...</QueryState>;
  }

  if (error) {
    return (
      <QueryState isError error={error}>
        ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຜັງອົງກອນ
      </QueryState>
    );
  }

  return (
    <>
      <Header />
      <Main className="flex h-[89vh] bg-background p-0">
        <div className="relative flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            nodesDraggable={false}
            fitView
            className="bg-background"
          >
            <Controls className="border bg-background" />
            <Background color="hsl(var(--muted))" gap={20} />

            {stats && (
              <Panel position="top-left" className="space-y-2">
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border border-border bg-card p-4">
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-card-foreground text-sm">
                          ພະນັກງານ
                        </p>
                        <p className="font-bold text-card-foreground text-lg">
                          {stats.totalEmployees}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="border border-border bg-card p-4">
                    <div className="flex items-center space-x-2">
                      <UserCheck className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-card-foreground text-sm">
                          ຕໍາແໜ່ງ
                        </p>
                        <p className="font-bold text-card-foreground text-lg">
                          {stats.totalPositions}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="border border-border bg-card p-4">
                    <div className="flex items-center space-x-2">
                      <Building className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-card-foreground text-sm">
                          ພະແນກ
                        </p>
                        <p className="font-bold text-card-foreground text-lg">
                          {stats.totalDepartments}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </Panel>
            )}

            <MiniMap
              nodeColor="hsl(var(--muted))"
              maskColor="hsl(var(--background)/0.8)"
              className="border bg-background"
            />
          </ReactFlow>
        </div>

        <OrgChartSidebar
          selectedPosition={selectedPosition}
          onClose={() => setSelectedPosition(null)}
        />
      </Main>
    </>
  );
}
